import {
  action,
  CookieConsentRequestAction,
  RegisteredEventName
} from '@finanzcheck/catalyst-pollard'
import { logAndTrackError } from 'utils/log/logAndTrackError'
import { logSuccess, logWarning } from 'utils/log'
import { isTenantSmava } from 'utils/url'
import pollard from 'utils/pollard'
import { setAmplitudeOptOut } from 'utils/tracking/amplitude'
import { StartupCommand } from './command.interface'

interface CookieConsentPayload {
  [serviceName: string]: boolean
}

enum Service {
  Amplitude = 'amplitude',
  GoogleAnalytics = 'googleAnalytics',
  Bugsnag = 'bugsnag'
}

const logServiceChange = (serviceName: string, isApproved: boolean) =>
  isApproved
    ? logSuccess(`[${serviceName}] enabled`)
    : logWarning(`[${serviceName}] disabled`)

const serviceHandlers: Partial<Record<Service, (isApproved: boolean) => void>> =
  {
    [Service.Amplitude]: (isApproved) => {
      if (isTenantSmava()) {
        setAmplitudeOptOut(true)
      } else {
        setAmplitudeOptOut(!isApproved)
        logServiceChange(Service.Amplitude, isApproved)
      }
    }
  }

function handleService(serviceName: string, isApproved: boolean) {
  const handler = serviceHandlers[serviceName]
  if (!handler) {
    logWarning(`[${serviceName}] No handler defined`)
    return
  }

  handler(isApproved)
}

function handleCookies(cookies: CookieConsentPayload) {
  for (const [serviceName, isApproved] of Object.entries(cookies)) {
    handleService(serviceName, isApproved)
  }
}

class InitCookiesCommand implements StartupCommand {
  name = 'InitCookies'
  dependencies = ['InitPollard']
  timeout = 5000

  execute = async () => {
    try {
      const { payload = {} }: CookieConsentRequestAction =
        await pollard.request(
          action<CookieConsentRequestAction>({ type: 'cookieConsentRequest' }),
          this.timeout
        )

      handleCookies(payload)
    } catch (error) {
      logAndTrackError(error as Error)
    }

    pollard.listenTo(RegisteredEventName.cookieConsentChange, handleCookies)
  }
}

export default InitCookiesCommand
